import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from 'react-query';
import { detailedDiff } from 'deep-object-diff';

import groupArrayItemsById from 'utils/groupArrayItemsById';

import ModalManager from '@ebay/nice-modal-react';

import ConfirmationModal from 'modals/ConfirmationModal';

import useUpdateListedKitDataByDiff from './useUpdateListedKitDataByDiff';

import { getMilestones, getAudits, detailedDiffForDocumentIds } from './index.helpers';

const useSaveAndPublishByDiffs = ({
  listedKit,
  defaultMilestones,
  milestonesAndAuditsSnapshot,
  tasksSnapshot,
  attachedSignDocumentIdsSnapshot,
  milestonesAndAuditsForChanges,
  tasksForChanges,
  milestonesWithChanges,
  attachedSignDocumentIdsForChanges,
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const {
    mutateAsync: updateListedKitDataByDiff,
    isLoading: isListedKitDataProcessing,
  } = useUpdateListedKitDataByDiff({ listedKit, defaultMilestones });

  const prepareDataForSaveAndPublish = useCallback(() => {
    const groupedTasksSnapshot = groupArrayItemsById(tasksSnapshot.current);
    const groupedTasksWithChanges = groupArrayItemsById(tasksForChanges);

    const groupedMilestonesSnapshot = groupArrayItemsById(
      getMilestones(milestonesAndAuditsSnapshot.current),
    );
    const groupedMilestonesWithChanges = groupArrayItemsById(
      getMilestones(milestonesAndAuditsForChanges),
    );

    const groupedAuditsSnapshot = groupArrayItemsById(
      getAudits(milestonesAndAuditsSnapshot.current),
    );
    const groupedAuditsWithChanges = groupArrayItemsById(
      getAudits(milestonesAndAuditsForChanges),
    );

    return {
      tasksDiff: detailedDiff(groupedTasksSnapshot, groupedTasksWithChanges),
      milestonesDiff: detailedDiff(groupedMilestonesSnapshot, groupedMilestonesWithChanges),
      auditsDiff: detailedDiff(groupedAuditsSnapshot, groupedAuditsWithChanges),
      attachedSignDocumentIdsDiff:
          detailedDiffForDocumentIds(attachedSignDocumentIdsSnapshot.current, attachedSignDocumentIdsForChanges),
    };
  }, [tasksSnapshot, tasksForChanges, milestonesAndAuditsSnapshot,
    milestonesAndAuditsForChanges, attachedSignDocumentIdsSnapshot, attachedSignDocumentIdsForChanges]);

  const handleSaveAndPublishClick = useCallback(async () => {
    const { tasksDiff, milestonesDiff, auditsDiff, attachedSignDocumentIdsDiff } = prepareDataForSaveAndPublish();

    const saveAndPublish = async () => {
      await updateListedKitDataByDiff({ tasksDiff, milestonesDiff, auditsDiff, attachedSignDocumentIdsDiff });
      [...Object.keys(tasksDiff.added), ...Object.keys(tasksDiff.updated)].map(
        (taskId) => queryClient.invalidateQueries(['documents.getByTaskId', taskId]),
      );

      queryClient.invalidateQueries('templates.defaultTemplates');
      queryClient.invalidateQueries('signnow.attachedSignDocuments');
    };

    const hasMilestoneWithoutTasks = [
      ...Object.keys(milestonesDiff.added),
      ...Object.keys(milestonesDiff.updated),
    ].some((milestoneId) => milestonesWithChanges.find((m) => m.id === milestoneId && m.tasks.length === 0));

    const milestonesWithChangesWithoutAudits = milestonesWithChanges.filter((m) => !m.isAudit);

    if (!milestonesWithChangesWithoutAudits.length) {
      return ModalManager.show(
        ConfirmationModal,
        {
          title: 'Are you sure?',
          text: 'Your template has no milestones saved.',
          confirmText: 'continue',
          onConfirm: saveAndPublish,
        },
      );
    }

    if (hasMilestoneWithoutTasks) {
      return ModalManager.show(
        ConfirmationModal,
        {
          title: 'Save incomplete milestone?',
          text: 'Are you sure you would like to save with a milestone with no assigned tasks? ',
          confirmText: 'Save anyway',
          onConfirm: saveAndPublish,
        },
      );
    }

    await saveAndPublish();
  }, [prepareDataForSaveAndPublish, updateListedKitDataByDiff, router, queryClient, milestonesWithChanges]);

  return {
    isListedKitDataProcessing,
    handleSaveAndPublishClick,
    prepareDataForSaveAndPublish,
  };
};

export default useSaveAndPublishByDiffs;
